'use client';

import { Box, Flex, IconButton, Input, Text, Textarea } from '@chakra-ui/react';
import { memo, useCallback, useMemo, useState } from 'react';
import { LuX } from 'react-icons/lu';

import EntityPicker from '@components/common/EntityPicker';
import CollectFromClientForm from '@features/finance/CollectFromClientForm';
import NewSupplierDialog from '@features/finance/NewSupplierDialog';
import PaySupplierForm from '@features/finance/PaySupplierForm';
import { allPaymentRequests, allSuppliers } from 'helpers/fakeData';
import {
  type ActionNodeData,
  type PaymentLinkNodeData,
  PaymentRoutingPolicy,
  type RoutingNodeData,
  type SupplierPayoutNodeData,
  type TriggerNodeData,
  type WorkflowNodeData,
  WorkflowNodeType,
} from '@features/workflows/types';

const POLICY_OPTIONS: Array<{ value: PaymentRoutingPolicy; label: string; hint: string }> = [
  {
    value: PaymentRoutingPolicy.ReleaseOnArrival,
    label: 'Release as funds arrive',
    hint: 'Pay the highest-ranked supplier as soon as the pool covers them.',
  },
  {
    value: PaymentRoutingPolicy.WaitForAll,
    label: 'Wait for all collections',
    hint: 'Hold until every linked payment link is fulfilled, then distribute.',
  },
];

type LinkTab = 'new' | 'existing';

const LINK_TABS: Array<{ value: LinkTab; label: string }> = [
  { value: 'new', label: 'Create new' },
  { value: 'existing', label: 'Link existing' },
];

const SegmentedToggle = ({
  value,
  onChange,
}: {
  value: LinkTab;
  onChange: (next: LinkTab) => void;
}) => (
  <Flex bg="surface.muted" borderRadius="md" p="2px" gap="2px">
    {LINK_TABS.map((tab) => {
      const active = value === tab.value;
      return (
        <Box
          key={tab.value}
          flex={1}
          textAlign="center"
          px={2}
          py={1}
          borderRadius="sm"
          cursor="pointer"
          bg={active ? 'surface.background' : 'transparent'}
          boxShadow={active ? 'xs' : 'none'}
          onClick={() => onChange(tab.value)}
        >
          <Text fontSize="xs" fontWeight="medium" color={active ? 'text.primary' : 'text.secondary'}>
            {tab.label}
          </Text>
        </Box>
      );
    })}
  </Flex>
);

// A payment link is either created here (CollectFromClientForm, the same inputs
// as the simplified builder) or linked to an existing link. Each existing link
// can feed only one routing pool, so taken links are filtered out.
const PaymentLinkConfig = ({
  nodeId,
  data,
  takenLinkIds,
  onUpdate,
}: {
  nodeId: string;
  data: PaymentLinkNodeData;
  takenLinkIds: string[];
  onUpdate: (nodeId: string, data: Partial<PaymentLinkNodeData>) => void;
}) => {
  const [tab, setTab] = useState<LinkTab>(data.linkId != null ? 'existing' : 'new');
  // Hold the raw amount string so partial/decimal entry (e.g. "120.") survives —
  // coercing number->string each render would strip the trailing dot mid-typing.
  // The popover remounts per node (keyed), so initialising from data is correct.
  const [amountStr, setAmountStr] = useState(data.targetAmount != null ? String(data.targetAmount) : '');

  const options = useMemo(
    () =>
      allPaymentRequests
        .filter((pr) => pr.id === data.linkId || !takenLinkIds.includes(pr.id))
        .map((pr) => ({
          id: pr.id,
          label: pr.title,
          secondary: `${pr.customerName} · ${pr.targetCurrency} ${pr.targetAmount.toLocaleString('en-US')}`,
        })),
    [takenLinkIds, data.linkId],
  );

  const onSelectExisting = useCallback(
    (id: string) => {
      const pr = allPaymentRequests.find((item) => item.id === id);
      if (pr == null) {
        return;
      }
      setAmountStr(String(pr.targetAmount));
      onUpdate(nodeId, {
        linkId: pr.id,
        label: pr.title,
        clientName: pr.customerName,
        targetAmount: pr.targetAmount,
        currency: pr.targetCurrency,
        allowPartial: pr.allowPartial,
      });
    },
    [nodeId, onUpdate],
  );

  const onAmountChange = useCallback(
    (value: string) => {
      setAmountStr(value);
      const n = Number(value);
      onUpdate(nodeId, { targetAmount: Number.isFinite(n) && n > 0 ? n : undefined });
    },
    [nodeId, onUpdate],
  );

  const onPickTab = useCallback(
    (next: LinkTab) => {
      setTab(next);
      // Switching back to a fresh link drops the existing-link association.
      if (next === 'new' && data.linkId != null) {
        onUpdate(nodeId, { linkId: undefined });
      }
    },
    [nodeId, onUpdate, data.linkId],
  );

  return (
    <Flex direction="column" gap={3}>
      <SegmentedToggle value={tab} onChange={onPickTab} />
      {tab === 'existing' ? (
        <Flex direction="column" gap={2}>
          <EntityPicker
            value={data.linkId}
            options={options}
            placeholder="Choose a payment link"
            createLabel="link"
            onSelect={onSelectExisting}
          />
          <Text fontSize="2xs" color="text.secondary">
            Each link can feed only one routing pool.
          </Text>
        </Flex>
      ) : (
        <CollectFromClientForm
          title={data.label}
          onTitleChange={(value) => onUpdate(nodeId, { label: value })}
          clientName={data.clientName ?? ''}
          onClientNameChange={(value) => onUpdate(nodeId, { clientName: value })}
          targetAmount={amountStr}
          onTargetAmountChange={onAmountChange}
          currency={data.currency ?? 'GHS'}
          onCurrencyChange={(value) => onUpdate(nodeId, { currency: value })}
          allowPartial={data.allowPartial ?? true}
          onAllowPartialChange={(value) => onUpdate(nodeId, { allowPartial: value })}
        />
      )}
    </Flex>
  );
};

// Reuses the same "pay supplier" inputs as the simplified builder.
const SupplierPayoutConfig = ({
  nodeId,
  data,
  onUpdate,
}: {
  nodeId: string;
  data: SupplierPayoutNodeData;
  onUpdate: (nodeId: string, data: Partial<SupplierPayoutNodeData>) => void;
}) => {
  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState('');
  // Raw amount string so decimals survive typing (see PaymentLinkConfig).
  const [amountStr, setAmountStr] = useState(data.amountUsdc != null ? String(data.amountUsdc) : '');

  const onSelectSupplier = useCallback(
    (id: string) => {
      const supplier = allSuppliers.find((item) => item.id === id);
      onUpdate(nodeId, {
        supplierId: id,
        label: supplier?.name ?? data.label,
        network: supplier?.network ?? data.network,
        walletAddress: supplier?.walletAddress ?? data.walletAddress,
        memo: supplier?.memo ?? data.memo,
      });
    },
    [nodeId, onUpdate, data.label, data.network, data.walletAddress, data.memo],
  );
  const onCreateSupplier = useCallback((query: string) => {
    setNewName(query);
    setNewOpen(true);
  }, []);
  const onNewOpenChange = useCallback((details: { open: boolean }) => {
    setNewOpen(details.open);
  }, []);
  const onAmountChange = useCallback(
    (value: string) => {
      setAmountStr(value);
      const n = Number(value);
      onUpdate(nodeId, { amountUsdc: Number.isFinite(n) && n > 0 ? n : undefined });
    },
    [nodeId, onUpdate],
  );
  const onNetworkChange = useCallback((value: string) => onUpdate(nodeId, { network: value }), [nodeId, onUpdate]);
  const onAddressChange = useCallback((value: string) => onUpdate(nodeId, { walletAddress: value }), [nodeId, onUpdate]);
  const onMemoChange = useCallback((value: string) => onUpdate(nodeId, { memo: value }), [nodeId, onUpdate]);

  return (
    <>
      <PaySupplierForm
        supplierId={data.supplierId ?? ''}
        onSelectSupplier={onSelectSupplier}
        onCreateSupplier={onCreateSupplier}
        amount={amountStr}
        onAmountChange={onAmountChange}
        network={data.network ?? ''}
        onNetworkChange={onNetworkChange}
        address={data.walletAddress ?? ''}
        onAddressChange={onAddressChange}
        memo={data.memo ?? ''}
        onMemoChange={onMemoChange}
      />
      <NewSupplierDialog open={newOpen} onOpenChange={onNewOpenChange} initialName={newName} />
    </>
  );
};

interface Props {
  nodeId: string;
  data: WorkflowNodeData;
  takenLinkIds: string[];
  onUpdate: (nodeId: string, data: Partial<WorkflowNodeData>) => void;
  onClose: () => void;
}

const TriggerConfig = ({
  nodeId,
  data,
  onUpdate,
}: {
  nodeId: string;
  data: TriggerNodeData;
  onUpdate: (nodeId: string, data: Partial<TriggerNodeData>) => void;
}) => {
  return (
    <Flex direction="column" gap={3}>
      <Flex direction="column" gap={1}>
        <Text fontSize="xs" fontWeight="medium" color="text.secondary">
          Label
        </Text>
        <Input
          size="sm"
          value={data.label}
          onChange={(e) => onUpdate(nodeId, { label: e.target.value })}
          borderColor="border.light"
          borderRadius="md"
          fontSize="sm"
        />
      </Flex>
      <Flex direction="column" gap={1}>
        <Text fontSize="xs" fontWeight="medium" color="text.secondary">
          Description
        </Text>
        <Textarea
          size="sm"
          value={data.description}
          onChange={(e) => onUpdate(nodeId, { description: e.target.value })}
          borderColor="border.light"
          borderRadius="md"
          fontSize="sm"
          rows={2}
          resize="none"
        />
      </Flex>
      <Flex direction="column" gap={1}>
        <Text fontSize="xs" fontWeight="medium" color="text.secondary">
          Trigger type
        </Text>
        <Text fontSize="sm" color="text.primary">
          {data.triggerType.replace(/_/g, ' ')}
        </Text>
      </Flex>
    </Flex>
  );
};

const ActionConfig = ({
  nodeId,
  data,
  onUpdate,
}: {
  nodeId: string;
  data: ActionNodeData;
  onUpdate: (nodeId: string, data: Partial<ActionNodeData>) => void;
}) => {
  return (
    <Flex direction="column" gap={3}>
      <Flex direction="column" gap={1}>
        <Text fontSize="xs" fontWeight="medium" color="text.secondary">
          Label
        </Text>
        <Input
          size="sm"
          value={data.label}
          onChange={(e) => onUpdate(nodeId, { label: e.target.value })}
          borderColor="border.light"
          borderRadius="md"
          fontSize="sm"
        />
      </Flex>
      <Flex direction="column" gap={1}>
        <Text fontSize="xs" fontWeight="medium" color="text.secondary">
          Description
        </Text>
        <Textarea
          size="sm"
          value={data.description}
          onChange={(e) => onUpdate(nodeId, { description: e.target.value })}
          borderColor="border.light"
          borderRadius="md"
          fontSize="sm"
          rows={2}
          resize="none"
        />
      </Flex>
      <Flex direction="column" gap={1}>
        <Text fontSize="xs" fontWeight="medium" color="text.secondary">
          Action type
        </Text>
        <Text fontSize="sm" color="text.primary" textTransform="capitalize">
          {data.actionType.replace(/_/g, ' ')}
        </Text>
      </Flex>
    </Flex>
  );
};

const RoutingConfig = ({
  nodeId,
  data,
  onUpdate,
}: {
  nodeId: string;
  data: RoutingNodeData;
  onUpdate: (nodeId: string, data: Partial<RoutingNodeData>) => void;
}) => {
  return (
    <Flex direction="column" gap={3}>
      <Flex direction="column" gap={1}>
        <Text fontSize="xs" fontWeight="medium" color="text.secondary">
          Label
        </Text>
        <Input
          size="sm"
          value={data.label}
          onChange={(e) => onUpdate(nodeId, { label: e.target.value })}
          borderColor="border.light"
          borderRadius="md"
          fontSize="sm"
        />
      </Flex>
      <Flex direction="column" gap={1}>
        <Text fontSize="xs" fontWeight="medium" color="text.secondary">
          Description
        </Text>
        <Textarea
          size="sm"
          value={data.description}
          onChange={(e) => onUpdate(nodeId, { description: e.target.value })}
          borderColor="border.light"
          borderRadius="md"
          fontSize="sm"
          rows={2}
          resize="none"
        />
      </Flex>
      <Flex direction="column" gap={1.5}>
        <Text fontSize="xs" fontWeight="medium" color="text.secondary">
          Disbursement policy
        </Text>
        {POLICY_OPTIONS.map((option) => {
          const active = (data.policy ?? PaymentRoutingPolicy.ReleaseOnArrival) === option.value;
          return (
            <Flex
              key={option.value}
              direction="column"
              gap={0.5}
              px={3}
              py={2}
              borderWidth="1px"
              borderColor={active ? 'selection.500' : 'border.light'}
              bg={active ? 'selection.50' : 'surface.background'}
              borderRadius="md"
              cursor="pointer"
              onClick={() => onUpdate(nodeId, { policy: option.value })}
            >
              <Text fontSize="sm" fontWeight="medium">
                {option.label}
              </Text>
              <Text fontSize="2xs" color="text.secondary">
                {option.hint}
              </Text>
            </Flex>
          );
        })}
      </Flex>
      <Flex direction="column" gap={1}>
        <Text fontSize="xs" fontWeight="medium" color="text.secondary">
          Suppliers · ranked ({data.paths.length})
        </Text>
        {data.paths.map((path, index) => (
          <Flex
            key={path.id}
            bg="surface.muted"
            px={2}
            py={1.5}
            borderRadius="md"
            direction="column"
            gap={0}
          >
            <Text fontSize="xs" fontWeight="medium">
              {index + 1}. {path.label}
            </Text>
            <Text fontSize="2xs" color="text.secondary">
              {path.amountUsdc != null ? `${path.amountUsdc.toLocaleString('en-US')} USDC` : path.description}
            </Text>
          </Flex>
        ))}
      </Flex>
    </Flex>
  );
};

const NodeConfigPopover = ({ nodeId, data, takenLinkIds, onUpdate, onClose }: Props) => {
  return (
    <Flex
      direction="column"
      bg="surface.background"
      borderWidth="1px"
      borderColor="border.light"
      borderRadius="xl"
      boxShadow="lg"
      w="300px"
      maxH="450px"
      overflow="hidden"
    >
      {/* Header */}
      <Flex px={3} pt={3} pb={2} justifyContent="space-between" alignItems="center">
        <Text fontSize="sm" fontWeight="semibold">
          Configure
        </Text>
        <IconButton
          size="2xs"
          variant="ghost"
          color="text.secondary"
          _hover={{ color: 'text.primary' }}
          onClick={onClose}
          aria-label="Close config"
        >
          <LuX size={14} />
        </IconButton>
      </Flex>
      {/* Config form */}
      <Box overflowY="auto" px={3} pb={3} flex={1}>
        {data.nodeType === WorkflowNodeType.Trigger && (
          <TriggerConfig nodeId={nodeId} data={data} onUpdate={onUpdate} />
        )}
        {data.nodeType === WorkflowNodeType.Action && (
          <ActionConfig nodeId={nodeId} data={data} onUpdate={onUpdate} />
        )}
        {data.nodeType === WorkflowNodeType.Routing && (
          <RoutingConfig nodeId={nodeId} data={data} onUpdate={onUpdate} />
        )}
        {data.nodeType === WorkflowNodeType.PaymentLink && (
          <PaymentLinkConfig
            nodeId={nodeId}
            data={data}
            takenLinkIds={takenLinkIds}
            onUpdate={onUpdate}
          />
        )}
        {data.nodeType === WorkflowNodeType.SupplierPayout && (
          <SupplierPayoutConfig nodeId={nodeId} data={data} onUpdate={onUpdate} />
        )}
      </Box>
    </Flex>
  );
};

export default memo(NodeConfigPopover);
